<template>
  <!-- "Katalog in deiner CI" (spec 2026-10-03 Lesbarkeit §7): the button in the brand kit and the view
       (editor v3, ansicht=katalog-ci) in a dialog. The CI it shows is the one being edited here. -->
  <!-- Embedded (review view of "CI von Webseite", spec 2026-10-03 §9.2): the view inline, no button, no dialog. -->
  <div v-if="eingebettet" class="kc-eingebettet-wrap" data-test="ci-katalog-eingebettet">
    <iframe v-if="buehne" ref="frame" :src="url" class="katalog-ci-frame kc-eingebettet" :title="t('katalog')" />
    <p v-else class="kc-ohne">{{ t('katalogOhneProjekt') }}</p>
  </div>
  <span v-else class="kc-knopf-wrap">
    <button
      type="button"
      class="kc-knopf"
      data-test="ci-katalog-knopf"
      :disabled="!buehne"
      :title="knopfTitel"
      @click="offen = true"
    >{{ t('katalog') }}</button>
    <b-modal
      v-if="offen && buehne"
      id="katalog-ci-modal"
      visible
      size="xl"
      modal-class="katalog-ci-modal"
      hide-footer
      hide-header
      @hidden="offen = false"
    >
      <iframe ref="frame" :src="url" class="katalog-ci-frame" :title="t('katalog')" />
    </b-modal>
  </span>
</template>

<script>
import GetKatalogCiBuehne from '@/graphQlQueries/queries/getKatalogCiBuehne';
import GetCustomCssOfFoldersAndProjects from '@/graphQlQueries/queries/getCustomCssOfFoldersAndProjects';
import joinCustomCss from '@/utils/joinCustomCss';
import loadSnippetsV2 from '@/utils/loadSnippetsV2';
import { katalogFuerSeite, preferOwnRows } from '@/utils/dbSnippetMapper';
import { filterLegacy } from '@/utils/dbElementMapper';
import { catalogVisibility, settingsForVersion } from '@/utils/catalogSwitch';
import { MODULE_ASSET_VERSION } from '@/utils/moduleAssetVersion';
import { cssMitBlock } from './ciVonWebseite';

/** Folder and project ids along an item path ("f13.f90.pr24"), for the customer CSS of the chain. */
function idsAusPfad(itemPath) {
  const teile = String(itemPath || '').split('.');
  return {
    folderIds: teile.filter((x) => /^f\d+$/.test(x)).map((x) => Number(x.slice(1))),
    projectIds: teile.filter((x) => /^pr\d+$/.test(x)).map((x) => Number(x.slice(2))),
  };
}

export default {
  name: 'KatalogCiKnopf',
  props: {
    // The CI as the brand kit holds it (also unsaved changes).
    ci: { type: Object, required: true },
    scope: { type: String, required: true },
    // Project id (scope project) or folder id (scope folder).
    itemId: { type: Number, default: null },
    // For the sidebar list (drafts, favourite flags), as the editor gets them.
    isSysAdmin: { type: Boolean, default: false },
    userId: { type: Number, default: null },
    // Inline in the review view of "CI von Webseite" instead of button + dialog.
    eingebettet: { type: Boolean, default: false },
    // The knob block of the import (§8.4) put at the top of the customer CSS; undefined = the CSS as saved.
    block: { type: String, default: undefined },
  },
  // vorlagen: undefined while loading, null when the sidebar list did not load (the view then shows its fallback).
  data: () => ({
    offen: false, buehne: null, kundenCss: '', vorlagen: undefined,
  }),
  apollo: {
    buehne: {
      query: GetKatalogCiBuehne,
      variables() {
        return {
          where: this.scope === 'folder'
            ? { parent_folder_id: { _eq: this.itemId } }
            : { project_id: { _eq: this.itemId } },
        };
      },
      update: (data) => (data.projects && data.projects[0]) || null,
      skip() { return !this.itemId; },
    },
    kundenCss: {
      query: GetCustomCssOfFoldersAndProjects,
      variables() { return idsAusPfad(this.cssPfad); },
      update(data) { return joinCustomCss(data.folders, data.projects, this.cssPfad); },
      skip() { return !this.buehne; },
    },
  },
  computed: {
    // On a folder the stage project is only theme and group: its own CSS is not the folder's, so the chain stops above it.
    cssPfad() {
      const pfad = this.buehne.item_path || '';
      return this.scope === 'folder' ? pfad.split('.').filter((x) => !/^pr\d+$/.test(x)).join('.') : pfad;
    },
    knopfTitel() {
      if (!this.buehne) return this.t('katalogOhneProjekt');
      return `${this.t('katalogTitel')}. ${this.$t('settings.ciBrandKit.katalogBuehne', { projekt: this.buehne.name })}`;
    },
    url() {
      const b = this.buehne;
      const seite = (b.child_pages && b.child_pages[0]) || null;
      const params = new URLSearchParams({
        ansicht: 'katalog-ci',
        schliessen: this.eingebettet ? '0' : '1',
        gruppe: (b.group && b.group.slug) || '',
        phpBase: process.env.VUE_APP_PHP_SERVER_ENDPOINT || '',
        collection: (b.template_collection && b.template_collection.name) || 'global_collection2',
        templateName: (seite && seite.template && seite.template.name) || 'global_article',
        theme: (b.theme && b.theme.name) || '',
        assetVersion: MODULE_ASSET_VERSION,
        uiLocale: this.$i18n.locale,
      });
      return `${process.env.VUE_APP_EDITOR_V2_ENDPOINT}/?${params.toString()}`;
    },
    herkunft() {
      return new URL(process.env.VUE_APP_EDITOR_V2_ENDPOINT).origin;
    },
  },
  watch: {
    ci: { deep: true, handler() { this.senden(); } },
    kundenCss() { this.senden(); },
    block() { this.senden(); },
    buehne(b) {
      // Embedded: the list loads with the stage (there is no opening).
      if (b && this.eingebettet && this.vorlagen === undefined) this.ladeVorlagen(b);
      // A link with ?ansicht=katalog-ci on the settings page opens the view at once (spec §7: the URL the AI returns).
      if (b && this.$route && this.$route.query.ansicht === 'katalog-ci') this.offen = true;
    },
    offen(ja) {
      // The admin's chat bubble sits above every z-index: hidden while the catalog is open.
      document.body.classList.toggle('kc-katalog-offen', ja);
      // The list loads with the first opening, not with every settings page.
      if (ja && this.vorlagen === undefined) this.ladeVorlagen(this.buehne);
    },
  },
  mounted() {
    window.addEventListener('message', this.beiNachricht);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.beiNachricht);
    document.body.classList.remove('kc-katalog-offen');
  },
  methods: {
    t(key) {
      return this.$t(`settings.ciBrandKit.${key}`);
    },
    /**
     * The list the editor sidebar shows this group on the stage project: the same loader (loadSnippetsV2) and the
     * same filters as KEditor.loadSnippetsForVue3 for a v3 page (legacy, own rows first, Bereich rows only).
     */
    async ladeVorlagen(b) {
      const settings = settingsForVersion(b.group && b.group.settings, b.editor_version);
      const catalog = catalogVisibility(settings, this.isSysAdmin);
      try {
        const zeilen = await loadSnippetsV2(this.$apollo, {
          groupId: b.group.group_id,
          userId: this.userId,
          language: this.$i18n.locale === 'en-US' ? 'en' : 'de',
          showDrafts: catalog.drafts,
          groupSettings: settings,
          projectId: b.project_id,
        });
        this.vorlagen = katalogFuerSeite(preferOwnRows(filterLegacy(zeilen, catalog.legacy)), true)
          // "Eigener Bereich" is empty and Profi only.
          .filter((c) => !c.nurProfi)
          .map((c) => ({
            key: c.name,
            name: { de: c.titleDe, en: c.titleEn },
            categories: c.categories,
            sort: c.sort,
            docs: c.bereiche || [c.section],
            // Saved templates show as saved; element templates may show every design.
            designs: !c.vorlage,
          }));
      } catch (err) {
        console.error('[KatalogCiKnopf] sidebar list did not load', err);
        this.vorlagen = null;
      }
      this.senden(true);
    },
    senden(mitVorlagen = false) {
      const { frame } = this.$refs;
      if (!frame || !frame.contentWindow) return;
      // A plain copy: Vue's reactive wrappers do not survive postMessage's structured clone.
      const css = this.block === undefined ? this.kundenCss || '' : cssMitBlock(this.kundenCss, this.block);
      const nachricht = { type: 'wm-ci-override', ci: JSON.parse(JSON.stringify(this.ci)), css };
      // The list goes once it is there (undefined: still loading); the view keeps it.
      if (mitVorlagen && this.vorlagen !== undefined) nachricht.templates = JSON.parse(JSON.stringify(this.vorlagen));
      frame.contentWindow.postMessage(nachricht, this.herkunft);
    },
    beiNachricht(event) {
      const { frame } = this.$refs;
      if (event.origin !== this.herkunft || !frame || event.source !== frame.contentWindow) return;
      const typ = event.data && event.data.type;
      if (typ === 'wm-catalog-ci-ready') this.senden(true);
      else if (typ === 'wm-catalog-ci-close') this.$bvModal.hide('katalog-ci-modal');
    },
  },
};
</script>

<style scoped>
.kc-knopf {
  all: unset; box-sizing: border-box; cursor: pointer; flex: none; display: inline-flex; align-items: center;
  height: 36px; padding: 0 16px; border-radius: 8px; background: #7fd6a4; color: #1d2b22;
  font-size: 14px; font-weight: 700; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25); transition: background 0.15s;
}
.kc-knopf:hover { background: #a6e4bf; }
.kc-knopf:focus-visible { outline: 2px solid #7fd6a4; outline-offset: 2px; }
.kc-eingebettet-wrap { border-radius: 12px; overflow: hidden; background: #36393d; }
.kc-eingebettet { height: 72vh; }
.kc-ohne { margin: 0; padding: 24px; font-size: 14px; color: #c9cbce; }
.kc-knopf:disabled { cursor: not-allowed; background: #5b6064; color: #a9acb1; box-shadow: none; }
</style>

<!-- unscoped on purpose: b-modal renders outside this component's root -->
<style>
.katalog-ci-modal .modal-dialog { max-width: 96vw; height: 94vh; margin: 3vh auto; }
.katalog-ci-modal .modal-content { height: 100%; overflow: hidden; border: 0; border-radius: 12px; }
.katalog-ci-modal .modal-body { padding: 0; height: 100%; }
.katalog-ci-frame { width: 100%; height: 100%; border: 0; display: block; }
body.kc-katalog-offen .crisp-client, body.kc-katalog-offen .crisp-fallback-bubble { display: none !important; }
@media (max-width: 600px) {
  .katalog-ci-modal .modal-dialog { max-width: 100vw; height: 100%; margin: 0; }
  .katalog-ci-modal .modal-content { border-radius: 0; }
}
</style>
